import { useState, useRef, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import styles from "./index.module.css";
import ClearIcon from "@mui/icons-material/Clear";
import axiosInstance from "../../../helpers/axios";

const OtpConsent = (props) => {
  const { openOtpConsent, submitUserOtpConsent, handleConsent, mobile } = props;
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    let interval;
    if (openOtpConsent && isResendDisabled) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsResendDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [openOtpConsent, isResendDisabled]);

  useEffect(() => {
    if (openOtpConsent && timer === 30) {
      sendOtp();
    }
  }, [openOtpConsent]);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otpDigits];
    newOtp[index] = value;
    setOtpDigits(newOtp);
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;
    
    const newOtp = [...otpDigits];
    pastedData.split("").forEach((char, idx) => {
      if (idx < 6) newOtp[idx] = char;
    });
    setOtpDigits(newOtp);
    
    const nextEmptyIndex = newOtp.findIndex((digit) => digit === "");
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputsRef.current[focusIndex]?.focus();
  };

  const sendOtp = async () => {
    const url = `/login/create-otp`;
    const payload = { mobile: mobile };
    try {
      await axiosInstance.post(url, payload);
    } catch (error) {
      alert("Error sending otp. ", error);
    }
  };

  const verifyOtp = async () => {
    setLoading(true);
    const url = `/login/verify-otp`;
    const payload = { mobile: mobile, otp: otpDigits?.join("") };
    try {
      const response = await axiosInstance.post(url, payload);
      if (response?.data?.message === "Successful") {
        submitUserOtpConsent();
      }
    } catch (e) {
      setLoading(false);
      alert("Error verifying otp. Please enter valid otp", e?.message);
    }
  };

  const handleResend = () => {
    if (!isResendDisabled) {
      sendOtp();
      setOtpDigits(["", "", "", "", "", ""]);
      setTimer(30);
      setLoading(false);
      setIsResendDisabled(true);
    }
  };

  return (
    <Dialog 
      open={openOtpConsent}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          margin: { xs: 2, sm: 3 },
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          maxWidth: { xs: '100%', sm: 500 }
        }
      }}
    >
      <DialogTitle className={styles.dialogTitle}>
        <Typography 
          className={styles.title}
          sx={{
            fontSize: { xs: '18px', sm: '20px' }
          }}
        >
          OTP Consent
        </Typography>
        <ClearIcon
          onClick={handleConsent}
          sx={{
            cursor: "pointer",
            fontSize: { xs: "20px", sm: "24px" },
          }}
        />
      </DialogTitle>
      
      <Typography
        variant="h6"
        textAlign="center"
        className={styles.info}
        sx={{
          mt: { xs: 1, sm: 2 },
          px: { xs: 2, sm: 3 },
          fontSize: { xs: '14px', sm: '16px' },
          lineHeight: 1.5
        }}
      >
        {`Enter the OTP sent to mobile no ${mobile}`}
      </Typography>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 }
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={{ xs: 1, sm: 2 }}
          mb={2}
          sx={{
            width: '100%',
            flexWrap: 'nowrap'
          }}
        >
          {otpDigits.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputsRef.current[index] = el)}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={index === 0 ? handlePaste : undefined}
              inputProps={{
                maxLength: 1,
                inputMode: "numeric",
                pattern: "[0-9]*",
                style: {
                  textAlign: "center",
                  fontSize: "20px",
                  fontFamily: "Outfit",
                  fontWeight: "bold",
                },
              }}
              variant="outlined"
              sx={{
                width: { xs: 40, sm: 50 },
                height: { xs: 45, sm: 55 },
                '& .MuiOutlinedInput-root': {
                  height: '100%',
                  '& input': {
                    padding: { xs: '8px', sm: '10px' }
                  }
                }
              }}
            />
          ))}
        </Box>
        
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 0.5, sm: 0 },
            mb: 2
          }}
        >
          <Typography 
            className={styles.resendText}
            sx={{
              fontSize: { xs: '13px', sm: '14px' },
              textAlign: 'center'
            }}
          >
            {isResendDisabled
              ? `You can resend OTP in ${timer} seconds`
              : "Didn't receive the OTP?"}
          </Typography>

          <Button
            onClick={handleResend}
            disabled={isResendDisabled}
            variant="text"
            className={styles.resendButton}
            sx={{
              fontSize: { xs: '13px', sm: '14px' },
              padding: { xs: '4px 8px', sm: '6px 16px' },
              minWidth: 'auto'
            }}
          >
            Resend OTP
          </Button>
        </Box>
        
        <Typography 
          className={styles.consetText}
          sx={{
            fontSize: { xs: '12px', sm: '14px' },
            lineHeight: 1.6,
            textAlign: 'center',
            px: { xs: 1, sm: 2 }
          }}
        >
          I consent and authorize{" "}
          <span
            style={{
              display: "inline",
              fontFamily: "Outfit",
            }}
          >
            <span
              style={{
                color: "#D32028",
                fontWeight: "bold",
                fontSize: "inherit",
              }}
            >
              fee
            </span>
            <span
              style={{
                color: "#000000",
                fontWeight: "bold",
                fontSize: "inherit",
              }}
            >
              monk
            </span>
          </span>{" "}
          to get a background check and a consumer credit report on me.
        </Typography>
      </DialogContent>
      
      {loading ? (
        <Button
          variant="contained"
          className={styles.loadingOtp}
          startIcon={<CircularProgress size={20} color="inherit" />}
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            py: { xs: 1.5, sm: 2 },
            mx: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3 }
          }}
        >
          Please wait...
        </Button>
      ) : (
        <Button
          onClick={verifyOtp}
          variant="contained"
          className={styles.submitOtp}
          disabled={otpDigits.some((digit) => digit === "")}
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            py: { xs: 1.5, sm: 2 },
            mx: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 3 }
          }}
        >
          Submit
        </Button>
      )}
    </Dialog>
  );
};

export default OtpConsent;